import { Alert, Group, List, Stack } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isTravelPaperTitleTakenError,
  toTravelDayBalancePreviewParams,
  toTravelPaperTitleCheckParams,
  useTravelPaperTitleCheck,
} from '../TravelRequestFormUtils';
import { getTravelDayBalancePreviewOptions, getTravelPaperTitleCheckOptions } from '../model/TravelRequestQuery';
import {
  TravelDocumentKinds,
  TravelRequest,
  exceededDayBalanceYears,
  travelRequestIssues,
} from '../model/TravelRequestTypes';
import { TravelPaperTitleAlert } from './TravelPaperTitleAlert';
import { TravelRequestSubmitButton } from './TravelRequestSubmitButton';
import { TravelRequestSubmitHints } from './TravelRequestSubmitHints';

interface TravelRequestSubmitStepProps {
  request: TravelRequest;
  documentKinds: TravelDocumentKinds | undefined;
}

export function TravelRequestSubmitStep({ request, documentKinds }: TravelRequestSubmitStepProps) {
  const { t } = useTranslation();
  // A failed preview drops only the day checks, since the backend repeats them at submit.
  const { data: preview, isError: previewFailed } = useQuery({
    ...getTravelDayBalancePreviewOptions(toTravelDayBalancePreviewParams(request, request.id)),
    retry: false,
  });
  const issues = travelRequestIssues(request, documentKinds, preview);
  const queryClient = useQueryClient();
  const paperTitleParams = toTravelPaperTitleCheckParams(request, request.id);
  const paperTitleCheck = useTravelPaperTitleCheck(paperTitleParams);

  const [refusedTitle, setRefusedTitle] = useState<string | null>(null);

  // The title may have been taken after the check last answered, so the refusal reloads it.
  const onSubmitError = (error: Error) => {
    if (isTravelPaperTitleTakenError(error) && paperTitleParams != null) {
      setRefusedTitle(paperTitleParams.paperTitle);
      queryClient.invalidateQueries({ queryKey: getTravelPaperTitleCheckOptions(paperTitleParams).queryKey });
    }
  };

  const warnings: string[] = [];
  if (preview?.enforcement.leaveDays === 'WARN' && preview.leaveDaysExceedWorkingDays) {
    warnings.push(
      t('travel.requests.issues.leaveDaysExceedWorkingDays', {
        leaveDays: preview.leaveDays,
        workingDays: preview.workingDays,
      }),
    );
  }
  if (preview?.enforcement.dayCap === 'WARN') {
    exceededDayBalanceYears(preview).forEach(values =>
      warnings.push(t('travel.requests.issues.dayCapExceeded', values)),
    );
  }

  return (
    <Stack gap="sm">
      <TravelRequestSubmitHints
        issues={issues}
        activityType={request.activityType}
        expenses={request.expenses}
      />
      {previewFailed && preview == null && (
        <Alert color="yellow">{t('travel.requests.dayBalance.checksUnavailable')}</Alert>
      )}
      {warnings.length > 0 && (
        <Alert
          color="yellow"
          title={t('travel.requests.view.dayBalanceWarn.title')}
        >
          <List size="sm">
            {warnings.map(warning => (
              <List.Item key={warning}>{warning}</List.Item>
            ))}
          </List>
        </Alert>
      )}
      <TravelPaperTitleAlert
        check={paperTitleCheck}
        refused={paperTitleParams != null && refusedTitle === paperTitleParams.paperTitle}
      />
      <Group>
        <TravelRequestSubmitButton
          requestId={request.id}
          disabled={issues.length > 0}
          resubmit={request.registration != null}
          onError={onSubmitError}
        />
      </Group>
    </Stack>
  );
}
